/**
 * The Shopify realization of the cart (SH5).
 *
 * On a Shopify storefront the cart of record is Shopify's own — checkout is
 * Shopify's, so the cart it charges must be the cart the shopper edited. This
 * driver runs on the merchant's page (the only place `/cart.js` is
 * same-origin) and does three things:
 *
 *   1. Drives Shopify's AJAX Cart API for the drawer, which asks over the
 *      panel → host channel (`HostCommand`) because it renders in our iframe.
 *   2. Takes over the theme's own cart triggers: clicks on cart links open our
 *      drawer, add-to-cart form submits go through us, and cart mutations the
 *      theme makes itself (fetch/XHR) are observed so the drawer and badge
 *      stay honest.
 *   3. Reports every mutation to the bridge (`shopify.cartEvent`) with
 *      Shopify's cart payload, so the store's abandoned-cart automations and
 *      reports see a cart that never touched Storedum's cart API.
 *
 * No React, no dependencies — this ships inside storedum.js.
 */
import {
    PROTOCOL_NAMESPACE,
    PROTOCOL_VERSION,
    type EventName,
    type HostCommand,
    type Reply,
    type ShopifyCart,
    type ShopifyCartEventInput,
    type ShopifyCartEventKind,
} from "@storedum/core";
import {
    chooseProvider,
    isExplicitProvider,
    whenReady,
    type CheckoutProvider,
    type ProviderWindow,
} from "./checkout-providers";

export interface ShopifyRoutes {
    root?: string;
    cart?: string;
    cartAdd?: string;
    cartChange?: string;
    cartUpdate?: string;
}

export interface ShopifyOptions {
    /** The shop's permanent domain (`*.myshopify.com`). */
    shop?: string;
    /** Shopify's customer id when the shopper is signed in to the shop. */
    customerId?: string | number | null;
    /** The theme's name (`theme.name` in Liquid), for per-theme overrides. */
    theme?: string | null;
    /** `routes.*_url` from Liquid — locale-prefixed shops need them. */
    routes?: ShopifyRoutes;
    /** Where Checkout sends the shopper. Default `<root>checkout`. */
    checkoutUrl?: string;
    /** `manual`: emit `cart:checkout` and stay put — the host navigates. */
    checkoutMode?: "shopify" | "manual";
    /** Open the drawer after an add-to-cart. Default true. */
    openOnAdd?: boolean;
    /** Take over clicks on the theme's cart links. Default true. */
    interceptCartLinks?: boolean;
    /** Take over the theme's add-to-cart forms. Default true. */
    interceptAddForms?: boolean;
    selectors?: {
        cartLinks?: string;
        addForms?: string;
    };
    /**
     * Theme sections to re-render after a change, as `section id → selector`
     * of the element whose contents to replace (Shopify's Section Rendering
     * API rides along on add/change). Default: Dawn's cart icon bubble.
     */
    sections?: Record<string, string>;
    /** The Storedum Sign-in app embed. Absent when that block is off. */
    signin?: ShopifySigninOptions;
}

export interface ShopifySigninOptions {
    /** Open sign-in from the theme's account and login links while the shopper is signed in nowhere. Default true. */
    accountLinks?: boolean;
    /** More elements that should open sign-in, for a theme whose account button the defaults miss. */
    selector?: string;
    /** Offer sign-in when a guest presses Checkout in the drawer; closing it continues to checkout. */
    beforeCheckout?: boolean;
    /** `routes.account_url` / `routes.account_login_url`, so locale-prefixed links are recognised. */
    accountUrl?: string;
    loginUrl?: string;
}

export interface SigninHooks {
    openLogin(): void;
    logout(): void;
}

export interface ShopifyHooks {
    openCart(): void;
    closePanel(): void;
    notify(event: EventName, payload: unknown): void;
    /** Tell an open drawer the cart changed under it. */
    refreshPanel(): void;
    /** Hand a mutation to the bridge for mirroring; must never throw. */
    report(input: ShopifyCartEventInput): void;
    /** Resolves once every queued mirror has reached the store. The drawer
     *  waits on this before re-reading, so Storedum's offer maths runs on a
     *  copy of the cart that matches Shopify's. */
    flush?(): Promise<void>;
    log(...args: unknown[]): void;
}

type Source = "panel" | "theme" | "sdk";

const DEFAULT_CART_LINKS =
    'a[href="/cart"], a[href$="/cart"], a[href^="/cart?"], a[href*="/cart?"], ' +
    "#cart-icon-bubble, [data-storedum-cart], [data-cart-drawer-toggle]";
const DEFAULT_ADD_FORMS = 'form[action$="/cart/add"], form[action*="/cart/add?"], form[action*="/cart/add."]';
const DEFAULT_SECTIONS: Record<string, string> = { "cart-icon-bubble": "#cart-icon-bubble" };

/**
 * Count badges set directly, for themes whose badge is not a section the
 * Section Rendering API can re-render. `hidden` is the class the theme hides
 * an empty badge with. Minimog only refreshes its bubbles inside its own
 * add-to-cart button, so without this the header kept the count the page
 * loaded with.
 */
const COUNT_BADGES: { selector: string; hidden?: string }[] = [
    { selector: ".m-cart-count-bubble", hidden: "m:hidden" },
    { selector: "[data-storedum-cart-count]" },
];

/** What a cart shows: lines, quantities, money. Attribute-only writes leave it alone. */
function cartSignature(cart: ShopifyCart): string {
    const lines = (cart.items || []).map((item) => `${item.key ?? item.id}:${item.quantity}:${item.final_line_price ?? ""}`);
    return [cart.token, cart.item_count, cart.total_price, ...lines].join("|");
}

/** A theme request that changes the cart — worth re-reading after. */
const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;

/**
 * The product template Storedum's catalog push gives a bundle box. That
 * product only carries the box page; what the shopper buys is the hidden
 * bundle of their picks that the page adds.
 */
const BOX_TEMPLATE = "storedum-box";

function trimSlash(value: string): string {
    return value.replace(/\/+$/, "");
}

/** The line that grew between two reads — what a theme-side add added. */
function grew(before: ShopifyCart | null, after: ShopifyCart): { variant_id: number; quantity: number } | undefined {
    const was = new Map<number, number>();
    for (const item of before?.items || []) was.set(item.variant_id ?? item.id, (was.get(item.variant_id ?? item.id) || 0) + item.quantity);
    for (const item of after.items || []) {
        const id = item.variant_id ?? item.id;
        const delta = item.quantity - (was.get(id) || 0);
        if (delta > 0) return { variant_id: id, quantity: delta };
    }
    return undefined;
}

function namedLine(id: unknown, quantity: unknown): { variant_id: number; quantity: number } | undefined {
    const variant_id = Number(id);
    const qty = Math.floor(Number(quantity) || 1);
    return Number.isFinite(variant_id) && variant_id > 0 ? { variant_id, quantity: Math.max(1, qty) } : undefined;
}

/**
 * The line a theme-side add request names in its own body — form data
 * (Dawn's product form), JSON (`{id, quantity}` or `{items: [...]}`) or a
 * query string. Exact, so no diff against a possibly stale snapshot is
 * needed; anything unreadable falls back to the diff.
 */
function addedLine(body: unknown): { variant_id: number; quantity: number } | undefined {
    try {
        if (!body) return undefined;
        if (typeof body === "string") {
            const text = body.trim();
            if (text.startsWith("{")) {
                const json = JSON.parse(text);
                const item = Array.isArray(json?.items) ? json.items[0] : json;
                return namedLine(item?.id, item?.quantity);
            }
            const params = new URLSearchParams(text);
            return namedLine(params.get("id"), params.get("quantity"));
        }
        if (body instanceof FormData || body instanceof URLSearchParams) return namedLine(body.get("id"), body.get("quantity"));
    } catch {
        /* not a body we understand — the diff still covers it */
    }
    return undefined;
}

export class ShopifyDriver {
    private readonly routes: Required<ShopifyRoutes>;
    private readonly sections: Record<string, string>;
    /** Our own requests in flight — the fetch observer must not echo them. */
    private own = 0;
    private syncTimer: ReturnType<typeof setTimeout> | null = null;
    private last: ShopifyCart | null = null;
    /** The cart before a theme-side change, held across the debounce. */
    private syncBefore: ShopifyCart | null | undefined = undefined;
    /** The line a theme-side add named in its request, when it could be read. */
    private syncHint: { variant_id: number; quantity: number } | undefined = undefined;
    /** `cartSignature` of the cart the drawer last received. */
    private servedSig = "";
    /** Product handle → whether it is a bundle box's own product, read once per page. */
    private readonly boxes = new Map<string, Promise<boolean>>();

    constructor(
        readonly options: ShopifyOptions,
        private readonly hooks: ShopifyHooks,
    ) {
        const r = options.routes || {};
        const root = r.root && r.root !== "/" ? trimSlash(r.root) : "";
        this.routes = {
            root: root || "/",
            cart: trimSlash(r.cart || `${root}/cart`),
            cartAdd: trimSlash(r.cartAdd || `${root}/cart/add`),
            cartChange: trimSlash(r.cartChange || `${root}/cart/change`),
            cartUpdate: trimSlash(r.cartUpdate || `${root}/cart/update`),
        };
        this.sections = options.sections || DEFAULT_SECTIONS;
    }

    /** The cart as last read — for a synchronous badge before any request. */
    get cart(): ShopifyCart | null {
        return this.last;
    }

    get busy(): boolean {
        return this.own > 0;
    }

    /** Record the cart the drawer was just handed, so a change it already shows is not pushed at it again. */
    served(cart: ShopifyCart | null | undefined): void {
        if (cart?.token) this.servedSig = cartSignature(cart);
    }

    private async call(url: string, body?: unknown): Promise<any> {
        this.own += 1;
        try {
            const response = await fetch(url, {
                method: body === undefined ? "GET" : "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: body === undefined ? undefined : JSON.stringify(body),
                credentials: "same-origin",
            });
            const data = await response.json().catch(() => null);
            if (!response.ok) {
                throw new Error(
                    data?.description || data?.message || `Shopify cart request failed (${response.status})`,
                );
            }
            return data;
        } finally {
            this.own -= 1;
        }
    }

    private sectionIds(): string | undefined {
        const ids = Object.keys(this.sections).filter((id) => document.querySelector(this.sections[id]));
        return ids.length ? ids.join(",") : undefined;
    }

    async get(): Promise<ShopifyCart> {
        const cart = (await this.call(`${this.routes.cart}.js`)) as ShopifyCart;
        this.last = cart;
        return cart;
    }

    async add(id: number, quantity = 1, properties?: Record<string, unknown>, source: Source = "sdk"): Promise<ShopifyCart> {
        const body: Record<string, unknown> = {
            items: [{ id, quantity, ...(properties && Object.keys(properties).length ? { properties } : {}) }],
        };
        const sections = this.sectionIds();
        if (sections) body.sections = sections;
        const added = await this.call(`${this.routes.cartAdd}.js`, body);
        // add.js answers with the lines it added, not the cart.
        const cart = await this.get();
        this.changed("add", cart, source, { variant_id: id, quantity }, added?.sections);
        return cart;
    }

    async change(id: string | number, quantity: number, source: Source = "sdk"): Promise<ShopifyCart> {
        // Shopify validates `id` as a STRING (digits, or a line key); a JSON
        // number is refused with "expected Integer to be a ...".
        const body: Record<string, unknown> = { id: String(id), quantity };
        const sections = this.sectionIds();
        if (sections) body.sections = sections;
        const cart = (await this.call(`${this.routes.cartChange}.js`, body)) as ShopifyCart & { sections?: any };
        this.last = cart;
        this.changed(quantity <= 0 ? "remove" : "change", cart, source, undefined, cart.sections);
        return cart;
    }

    /** Report the cart unchanged — after a sign-in, so the store's copy moves onto the shopper. */
    resend(cart: ShopifyCart): void {
        this.hooks.report(this.event("sync", cart));
    }

    /** Read the cart and report it as `kind` — a checkout starting changes nothing in it, but is a funnel step. */
    async report(kind: ShopifyCartEventKind): Promise<ShopifyCart> {
        const cart = await this.get();
        this.hooks.report(this.event(kind, cart));
        return cart;
    }

    async attributes(attributes: Record<string, string>): Promise<ShopifyCart> {
        const cart = (await this.call(`${this.routes.cartUpdate}.js`, { attributes })) as ShopifyCart;
        this.last = cart;
        this.hooks.report(this.event("sync", cart));
        return cart;
    }

    /** Re-read after a change we did not make ourselves (the theme's own JS). */
    sync(kind: ShopifyCartEventKind = "sync", hint?: { variant_id: number; quantity: number }): void {
        // The "before" is the cart as it stood when the change was NOTICED:
        // the drawer opening on this very add can re-read the cart inside the
        // debounce window, and a snapshot taken then already holds the line.
        if (this.syncBefore === undefined) this.syncBefore = this.last;
        if (hint) this.syncHint = hint;
        if (this.syncTimer) clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(() => {
            this.syncTimer = null;
            const before = this.syncBefore ?? null;
            const named = this.syncHint;
            this.syncBefore = undefined;
            this.syncHint = undefined;
            Promise.all([this.get(), this.fetchSections()])
                .then(([cart, sections]) => this.changed(kind, cart, "theme", kind === "add" ? named || grew(before, cart) : undefined, sections))
                .catch((error) => this.hooks.log("cart sync failed", error));
        }, 150);
    }

    /**
     * The theme's cart badge, re-rendered through Shopify's Section Rendering
     * API — for changes made by the theme's own requests, which carried no
     * `sections` of ours. Failure is not an error: the badge is cosmetic.
     */
    private async fetchSections(): Promise<Record<string, string | null> | undefined> {
        const ids = this.sectionIds();
        if (!ids) return undefined;
        try {
            const response = await fetch(`${this.routes.root}?sections=${encodeURIComponent(ids)}`, {
                headers: { Accept: "application/json" },
                credentials: "same-origin",
            });
            return response.ok ? await response.json() : undefined;
        } catch {
            return undefined;
        }
    }

    /**
     * Hand the shopper to the store's checkout.
     *
     * `choice` is the cart designer's Checkout setting ("auto" when absent):
     * the store's checkout app (GoKwik, …) when it is on this page, otherwise
     * Shopify's checkout. A Checkout URL set in the theme editor is deliberate
     * and wins over "auto" — only an explicit pick in the designer beats it.
     */
    checkout(discountCode?: string, choice?: string): void {
        if (this.options.checkoutMode === "manual") return;
        const provider =
            this.options.checkoutUrl && !isExplicitProvider(choice)
                ? null
                : chooseProvider(choice, window as ProviderWindow, document);
        if (provider) {
            void this.openProvider(provider, discountCode);
            return;
        }
        this.shopifyCheckout(discountCode);
    }

    /**
     * Open a third-party checkout. It reads the page's Shopify cart — the
     * same cart the drawer edited — so nothing but the coupon has to travel.
     * If the app never finishes loading, or throws, the shopper still gets
     * Shopify's checkout rather than a dead button.
     */
    private async openProvider(provider: CheckoutProvider, discountCode?: string): Promise<void> {
        const code = String(discountCode || "").trim();
        if (code) await this.applyDiscount(code);
        const win = window as ProviderWindow;
        if (!(await whenReady(provider, win))) {
            this.hooks.log(`checkout: ${provider.label} did not load in time — using Shopify checkout`);
            this.shopifyCheckout(discountCode);
            return;
        }
        try {
            provider.open(win);
        } catch (error) {
            this.hooks.log(`checkout: ${provider.label} failed to open — using Shopify checkout`, error);
            this.shopifyCheckout(discountCode);
        }
    }

    /**
     * Put the drawer's coupon on the Shopify cart session before a
     * third-party checkout reads the cart: Shopify's `/discount/<code>` route
     * stores it exactly as a storefront discount link does. Best effort and
     * bounded — the checkout never waits long on it, and a code the app
     * ignores can still be entered in its own coupon field.
     */
    private async applyDiscount(code: string): Promise<void> {
        const root = trimSlash(this.routes.root);
        const url = `${root}/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root}/cart.js`)}`;
        try {
            await Promise.race([
                fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } }),
                new Promise((resolve) => setTimeout(resolve, 2000)),
            ]);
        } catch {
            /* the shopper can still enter it in the checkout */
        }
    }

    /**
     * Hand the shopper to Shopify's checkout.
     *
     * A discount code goes through Shopify's own `/discount/<code>` route,
     * which applies it to the cart session and then forwards to checkout. That
     * way the code is validated and priced by Shopify — the drawer never has
     * to promise a discount Shopify has not agreed to.
     */
    private shopifyCheckout(discountCode?: string): void {
        const root = trimSlash(this.routes.root);
        const target = this.options.checkoutUrl || `${root}/checkout`;
        const code = String(discountCode || "").trim();
        if (code && !this.options.checkoutUrl) {
            const redirect = target.startsWith("http") ? target : target || "/checkout";
            window.location.assign(`${root}/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`);

            return;
        }
        window.location.assign(target);
    }

    private event(event: ShopifyCartEventKind, cart: ShopifyCart, changed?: { variant_id: number; quantity: number }): ShopifyCartEventInput {
        return {
            event,
            cart,
            changed,
            customer_id: this.options.customerId ?? null,
            shop: this.options.shop,
        };
    }

    private changed(
        event: ShopifyCartEventKind,
        cart: ShopifyCart,
        source: Source,
        changed?: { variant_id: number; quantity: number },
        sections?: Record<string, string | null>,
    ): void {
        if (sections) this.renderSections(sections);
        this.updateBadges(cart.item_count ?? 0);
        const summary = { total_items: cart.item_count ?? 0, total_price: (cart.total_price ?? 0) / 100 };
        try {
            document.dispatchEvent(new CustomEvent("storedum:cart:updated", { detail: { ...summary, cart } }));
        } catch {
            /* an old browser without CustomEvent constructors — the SDK event still fires */
        }
        this.hooks.notify("cart:updated", summary);
        // The drawer re-reads after its own mutations; only a change it did
        // not make needs to be pushed at it — and not one that leaves the cart
        // as the drawer already shows it (an app writing cart attributes).
        if (source !== "panel" && cartSignature(cart) !== this.servedSig) this.hooks.refreshPanel();
        this.hooks.report(this.event(event, cart, changed));
        if (event === "add") void this.dropEmptyBoxes(cart, changed?.variant_id);
    }

    private isBox(handle: string): Promise<boolean> {
        let known = this.boxes.get(handle);
        if (!known) {
            known = fetch(`${trimSlash(this.routes.root)}/products/${encodeURIComponent(handle)}.json`, {
                headers: { Accept: "application/json" },
                credentials: "same-origin",
            })
                .then((response) => (response.ok ? response.json() : null))
                .then((data) => data?.product?.template_suffix === BOX_TEMPLATE)
                .catch(() => false);
            this.boxes.set(handle, known);
        }
        return known;
    }

    /**
     * A theme's own quick add (a collection card, search results) can still put
     * a bundle box's product in the cart with nothing in it. Take it out again
     * and send the shopper to the box page to fill it.
     */
    private async dropEmptyBoxes(cart: ShopifyCart, added?: number): Promise<void> {
        try {
            const lines = (cart.items || []).filter(
                (item) => item.handle && item.key && (added === undefined || (item.variant_id ?? item.id) === added),
            );
            const flags = await Promise.all(lines.map((item) => this.isBox(item.handle as string)));
            const empty = lines.filter((_, index) => flags[index]);
            if (!empty.length) return;
            for (const item of empty) await this.change(item.key as string, 0);
            const page = (empty[0].url || `${trimSlash(this.routes.root)}/products/${empty[0].handle}`).split("?")[0];
            this.hooks.log("an empty bundle box left the cart; opening its page to fill it", empty[0].handle);
            if (trimSlash(window.location.pathname) === trimSlash(page)) {
                this.hooks.closePanel();
                document.querySelector("[data-storedum-box]")?.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }
            window.location.assign(page);
        } catch (error) {
            this.hooks.log("empty box check failed", error);
        }
    }

    private updateBadges(count: number): void {
        for (const badge of COUNT_BADGES) {
            document.querySelectorAll<HTMLElement>(badge.selector).forEach((el) => {
                if (count > 0) {
                    el.textContent = String(count);
                    if (badge.hidden) el.classList.remove(badge.hidden);
                } else if (badge.hidden) {
                    el.classList.add(badge.hidden);
                } else {
                    el.textContent = "0";
                }
            });
        }
    }

    /**
     * Swap re-rendered theme sections in place — how Dawn keeps its cart
     * bubble current without a reload. A section the response did not carry
     * (a theme without it) is simply skipped.
     */
    private renderSections(sections: Record<string, string | null>): void {
        for (const id of Object.keys(sections)) {
            const html = sections[id];
            const target = this.sections[id] ? document.querySelector(this.sections[id]) : null;
            if (!html || !target) continue;
            try {
                const doc = new DOMParser().parseFromString(html, "text/html");
                const inner = doc.querySelector(".shopify-section") || doc.body;
                target.innerHTML = inner.innerHTML;
            } catch {
                /* leave the theme's markup alone rather than half-replace it */
            }
        }
    }
}

/** Read an add-to-cart form the way Shopify's `/cart/add` would. */
function readAddForm(form: HTMLFormElement): { id: number; quantity: number; properties: Record<string, unknown> } | null {
    const data = new FormData(form);
    const id = Number(data.get("id"));
    if (!id) return null;
    const quantity = Math.max(1, Number(data.get("quantity")) || 1);
    const properties: Record<string, unknown> = {};
    data.forEach((value, key) => {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (match && typeof value === "string" && value !== "") properties[match[1]] = value;
    });
    return { id, quantity, properties };
}

/**
 * Take over the theme's cart triggers and watch its own cart traffic.
 *
 * Capture phase, on the document: the theme's handlers sit on the elements
 * themselves, so ours run first and can stop theirs — otherwise Dawn would
 * open its drawer beside ours, or navigate to /cart under it.
 */
export function installShopify(options: ShopifyOptions, hooks: ShopifyHooks): ShopifyDriver {
    const driver = new ShopifyDriver(options, hooks);
    // One read up front, so a change the theme makes on its own can be
    // diffed against what the cart held before it — and the badge has a
    // number before the first open.
    driver.get().catch(() => {});
    const selectors = {
        cartLinks: options.selectors?.cartLinks || DEFAULT_CART_LINKS,
        addForms: options.selectors?.addForms || DEFAULT_ADD_FORMS,
    };
    const openOnAdd = options.openOnAdd !== false;

    if (options.interceptCartLinks !== false) {
        document.addEventListener(
            "click",
            (event) => {
                const target = event.target as Element | null;
                const link = target?.closest?.(selectors.cartLinks);
                if (!link) return;
                // Modifier clicks mean "new tab" — the shopper asked for the
                // page, not the drawer.
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                event.preventDefault();
                event.stopImmediatePropagation();
                hooks.openCart();
            },
            true,
        );
    }

    if (options.interceptAddForms !== false) {
        document.addEventListener(
            "submit",
            (event) => {
                const form = event.target as HTMLFormElement | null;
                if (!(form instanceof HTMLFormElement) || !form.matches(selectors.addForms)) return;
                const input = readAddForm(form);
                if (!input) return;
                event.preventDefault();
                event.stopImmediatePropagation();
                const button = form.querySelector<HTMLButtonElement>('[type="submit"], button:not([type])');
                if (button) button.setAttribute("aria-busy", "true");
                driver
                    .add(input.id, input.quantity, input.properties, "theme")
                    .then(() => {
                        if (openOnAdd) hooks.openCart();
                    })
                    .catch((error) => {
                        hooks.log("add to cart failed", error);
                        hooks.notify("bridge:error", { message: error?.message || "Could not add to cart" });
                        // Our take-over failed; let the theme have its turn.
                        form.submit();
                    })
                    .then(() => button?.removeAttribute("aria-busy"));
            },
            true,
        );
    }

    // The theme's own cart traffic (a quick-add button, a "buy it again"
    // widget) changes the cart without telling anyone. Watch both transports.
    const nativeFetch = window.fetch;
    window.fetch = function (this: unknown, input: RequestInfo | URL, init?: RequestInit) {
        const url = typeof input === "string" ? input : input instanceof URL ? input.href : input?.url || "";
        const result = nativeFetch.call(this, input, init);
        if (!driver.busy && CART_MUTATION.test(url)) {
            const isAdd = /\/cart\/add/.test(url);
            const hint = isAdd ? addedLine(init?.body) : undefined;
            result.then(
                (response) => {
                    if (response.ok) driver.sync(isAdd ? "add" : "sync", hint);
                    if (response.ok && openOnAdd && isAdd) hooks.openCart();
                },
                () => {},
            );
        }
        return result;
    } as typeof window.fetch;

    type CartXhr = XMLHttpRequest & { __storedumCart?: string };
    const nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (this: CartXhr, method: string, url: string | URL, ...rest: any[]) {
        const href = typeof url === "string" ? url : url.href;
        this.__storedumCart = CART_MUTATION.test(href) ? href : undefined;
        return (nativeOpen as any).call(this, method, url, ...rest);
    } as typeof XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (this: CartXhr, body?: Document | XMLHttpRequestBodyInit | null) {
        const href = this.__storedumCart;
        if (href) {
            const isAdd = /\/cart\/add/.test(href);
            const hint = isAdd ? addedLine(body) : undefined;
            this.addEventListener("load", () => {
                if (this.status < 400 && !driver.busy) driver.sync(isAdd ? "add" : "sync", hint);
            });
        }
        return nativeSend.call(this, body);
    };

    return driver;
}

/** The product a product page is about, from the analytics meta Shopify puts on every storefront. */
export function viewedProduct(): { product: string; variant?: string } | null {
    try {
        const id = (window as any).ShopifyAnalytics?.meta?.product?.id;
        if (!id) return null;
        const variant = new URLSearchParams(window.location.search).get("variant") || "";
        return { product: String(id), variant: /^\d+$/.test(variant) ? variant : undefined };
    } catch {
        return null;
    }
}

/** Shopify's consent answer for analytics, where the store runs a consent banner; allowed otherwise. */
export function analyticsAllowed(): boolean {
    try {
        const privacy = (window as any).Shopify?.customerPrivacy;
        return typeof privacy?.analyticsProcessingAllowed === "function" ? privacy.analyticsProcessingAllowed() !== false : true;
    } catch {
        return true;
    }
}

const SIGNIN_TRIGGER = "[data-storedum-login]";
const SIGNOUT_TRIGGER = "[data-storedum-logout]";
/** Sign-in and account-root paths, optionally locale-prefixed — never logout, never a sub-page like orders. */
const ACCOUNT_PATH = /^(\/[a-z]{2,3}(-[a-z0-9]{2,4})?)?\/(account|account\/login|account\/register|customer_authentication\/redirect|customer_authentication\/login)$/i;
/** New customer accounts: shopify.com/<shop id>/account. */
const HOSTED_ACCOUNT_PATH = /^\/\d+\/account$/;

function pathOf(value: string | undefined): string | null {
    if (!value) return null;
    try {
        return new URL(value, window.location.href).pathname.replace(/\/+$/, "");
    } catch {
        return null;
    }
}

function isAccountLink(anchor: HTMLAnchorElement, signin: ShopifySigninOptions): boolean {
    let url: URL;
    try {
        url = new URL(anchor.getAttribute("href") || "", window.location.href);
    } catch {
        return false;
    }
    const path = url.pathname.replace(/\/+$/, "");
    if (url.origin === window.location.origin) {
        return ACCOUNT_PATH.test(path) || path === pathOf(signin.accountUrl) || path === pathOf(signin.loginUrl);
    }
    return url.hostname === "shopify.com" && HOSTED_ACCOUNT_PATH.test(path);
}

function safeClosest(target: Element, selector: string): Element | null {
    try {
        return target.closest(selector);
    } catch {
        return null;
    }
}

/**
 * Storedum sign-in on a Shopify theme: the theme's account and login links,
 * any element marked `data-storedum-login`, and `?signin=1` open the Signet
 * panel; `data-storedum-logout` signs out.
 *
 * Account links are taken unless the shopper is logged in to the shop's own
 * customer account, where the link leads to their Shopify orders. A shopper
 * signed in to Storedum still gets the panel: the theme's account page would
 * only send them to Shopify's login.
 */
export function installSignin(options: ShopifyOptions, hooks: SigninHooks): void {
    const signin = options.signin || {};
    const extra = (signin.selector || "").trim();

    document.addEventListener(
        "click",
        (event) => {
            const target = event.target as Element | null;
            if (!target?.closest) return;
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
            const signout = target.closest(SIGNOUT_TRIGGER);
            let take = !!signout || !!target.closest(SIGNIN_TRIGGER);
            if (!take && !options.customerId) {
                if (extra && safeClosest(target, extra)) take = true;
                else if (signin.accountLinks !== false) {
                    const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
                    take = !!anchor && isAccountLink(anchor, signin);
                }
            }
            if (!take) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            if (signout) hooks.logout();
            else hooks.openLogin();
        },
        true,
    );

    // A link in a message can land the shopper straight on the panel. Stripped
    // so a reload does not open it again.
    try {
        const url = new URL(window.location.href);
        if (url.searchParams.get("signin") === "1") {
            url.searchParams.delete("signin");
            window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
            hooks.openLogin();
        }
    } catch {
        /* no URL API — the page simply opens without the panel */
    }
}

/** Answer a drawer's request against the Shopify cart. */
export function serveHostCommand(driver: ShopifyDriver, command: HostCommand, source: Window, origin: string, hooks: ShopifyHooks): void {
    const respond = (reply: Reply) => source.postMessage(reply, origin);
    const payload: any = command.payload || {};
    const run = async (): Promise<unknown> => {
        switch (command.host) {
            case "host.cart.get": {
                const cart = await driver.get();
                driver.served(cart);

                return cart;
            }
            case "host.cart.add": {
                const cart = await driver.add(Number(payload.id), Number(payload.quantity) || 1, payload.properties, "panel");
                driver.served(cart);
                // Settle the mirror before answering: the drawer reads the
                // store's offers straight after this and they are computed
                // from the mirrored cart.
                await hooks.flush?.();

                return cart;
            }
            case "host.cart.change": {
                const cart = await driver.change(payload.id, Number(payload.quantity) || 0, "panel");
                driver.served(cart);
                await hooks.flush?.();

                return cart;
            }
            case "host.cart.attributes": {
                const cart = await driver.attributes(payload.attributes || {});
                driver.served(cart);
                await hooks.flush?.();

                return cart;
            }
            case "host.cart.flush":
                await hooks.flush?.();

                return { ok: true };
            case "host.checkout":
                hooks.closePanel();
                driver.checkout(payload.discount_code, payload.checkout_provider);
                return { ok: true };
            default:
                throw new Error(`Unknown host command "${(command as HostCommand).host}"`);
        }
    };
    run().then(
        (data) => respond({ ns: PROTOCOL_NAMESPACE, v: PROTOCOL_VERSION, id: command.id, ok: true, data }),
        (error: unknown) =>
            respond({
                ns: PROTOCOL_NAMESPACE,
                v: PROTOCOL_VERSION,
                id: command.id,
                ok: false,
                error: error instanceof Error ? error.message : "Shopify cart request failed",
            }),
    );
}
